import { useEffect, useState } from 'react';
import { isEmptyArray, isNil, isNotNil, type Nilable } from '~/utilities/type-guards';
import {
  type ContactIdentity,
  type ContactIdentityVerification,
  type ContactIdentityVerificationData,
  type ContactIdentityVerificationError,
  upsertContactIdentity,
} from './contactIdentity';

type VerifyContactIdentityProps = {
  accountHashedId?: string;
  contactIdentities: ContactIdentity[];
  embedHashedId?: string;
  appHost: Nilable<string>;
  embedType?: string;
  skip: boolean;
};

type VerifyContactIdentityResult = ContactIdentityVerification & {
  error: string | null;
  isLoading: boolean;
};

const defaultContactVerification: ContactIdentityVerification = {
  contactIdentity: null,
  restricted: false,
  registered: false,
};

const verifyContactIdentity = async (
  data: ContactIdentityVerificationData,
  appHost: string,
): Promise<ContactIdentityVerification> => {
  const response = await fetch(`https://${appHost}/contact_identities/verify`, {
    method: 'POST',
    body: JSON.stringify(data),
    headers: {
      'Content-Type': 'application/json',
    },
  });
  const responseJson = (await response.json()) as
    | ContactIdentityVerification
    | ContactIdentityVerificationError;

  if (!response.ok) {
    const { error } = responseJson as ContactIdentityVerificationError;
    return Promise.reject(new Error(error));
  }

  return responseJson as ContactIdentityVerification;
};

export const useVerifyContactIdentities = ({
  accountHashedId,
  contactIdentities,
  embedHashedId,
  embedType,
  appHost,
  skip = false,
}: VerifyContactIdentityProps): VerifyContactIdentityResult => {
  const [verifiedContactIdentity, setVerifiedContactIdentity] =
    useState<ContactIdentityVerification>(defaultContactVerification);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (skip || isEmptyArray(contactIdentities) || isNil(accountHashedId) || isNil(appHost)) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    const verifyContactIdentities = async () => {
      try {
        const result = await verifyContactIdentity(
          {
            accountHashedId,
            contactIdentities,
            embedHashedId,
            embedType,
          },
          appHost,
        );
        setVerifiedContactIdentity(result);

        if (!result.restricted && isNotNil(result.contactIdentity)) {
          upsertContactIdentity(result.contactIdentity);
        }
      } catch (err: unknown) {
        if (err instanceof Error) {
          setError(err.message);
        } else {
          setError('An unknown error occurred verifying the contact identity');
        }
      } finally {
        setIsLoading(false);
      }
    };

    void verifyContactIdentities();
  }, [skip, contactIdentities, accountHashedId, appHost, embedHashedId, embedType]);

  return { isLoading, error, ...verifiedContactIdentity };
};
